import { z } from 'zod';

/**
 * Full 4-value enum per the 2026-08-06 design doc (session 3, E4) even
 * though only `resize`/`toggle_visibility` are actually triggered this
 * pass — `cell_edit` (already real, see D10) and `regenerate` (not built
 * yet) stay non-undoable, matching the same "full locked shape now" choice
 * made for `cells`/`cell_versions`.
 */
export const actionTypeSchema = z.enum(['cell_edit', 'regenerate', 'resize', 'toggle_visibility']);
export type ActionType = z.infer<typeof actionTypeSchema>;

/**
 * `GET /matrices/:id/actions` — the raw actions log, newest-first, backing
 * the drawer's Historique tab in its **global** scope (2026-08-06 design
 * doc, M8). Today that log only ever contains `resize`/`toggle_visibility`
 * entries (E3), so this feed deliberately does *not* show manual edits or
 * regenerations — those surface in the per-cell scopes, via `cell_versions`.
 * `actorDisplayName` is resolved server-side (joined from `users`) so the
 * client doesn't need a second lookup to render "· Camille B."-style
 * attribution.
 */
export const actionLogEntrySchema = z.object({
  id: z.string().uuid(),
  matrixId: z.string().uuid(),
  actionType: actionTypeSchema,
  payload: z.record(z.string(), z.unknown()),
  actorId: z.string().uuid().nullable(),
  actorDisplayName: z.string().nullable(),
  createdAt: z.string().datetime(),
  /** True for the entry the undo/redo cursor currently points at (E5). */
  isCurrent: z.boolean(),
});
export type ActionLogEntry = z.infer<typeof actionLogEntrySchema>;

export const actionLogSchema = z.array(actionLogEntrySchema);
export type ActionLog = z.infer<typeof actionLogSchema>;

// ── resize ───────────────────────────────────────────────────────────────────

export const resizeTargetSchema = z.enum(['column', 'row', 'all']);
export type ResizeTarget = z.infer<typeof resizeTargetSchema>;

/**
 * `POST /matrices/:id/resize` body. `ref` is the palier number (as a
 * string) for `target: 'column'`, or `${rowType}:${rowRef}` for `target:
 * 'row'` — matching `MatrixViewStateShape.columnWidths`/`rowHeights`' keys.
 * Both `ref` and `sizePx` are omitted for `target: 'all'` (session 5's
 * "Réinitialiser les largeurs/hauteurs" bulk reset — there's no single
 * ref/size, it clears every custom width and height at once), which
 * mirrors `PerformVisibilityInput`'s existing `'all'`/omit-`hidden` shape.
 */
export const performResizeInputSchema = z.object({
  target: resizeTargetSchema,
  ref: z.string().min(1).optional(),
  sizePx: z.number().int().positive().max(4000).optional(),
});
export type PerformResizeInput = z.infer<typeof performResizeInputSchema>;

// ── visibility ───────────────────────────────────────────────────────────────

export const visibilityTargetSchema = z.enum(['activation', 'column', 'dimension', 'all']);
export type VisibilityTarget = z.infer<typeof visibilityTargetSchema>;

/**
 * `POST /matrices/:id/visibility` body. `ref` is `null` for `target:
 * 'activation'` and `target: 'all'` (there's nothing to identify — it's
 * the whole block, or everything at once); the palier number (as a string)
 * for `target: 'column'`; the `dimensions.id` for `target: 'dimension'`.
 * `hidden` is required for `activation`/`column`/`dimension` (the new
 * hidden/collapsed state to set) and omitted for `target: 'all'`, which
 * always means "un-hide/un-collapse everything" (E12) — there's no
 * "hide everything" operation.
 */
export const performVisibilityInputSchema = z.object({
  target: visibilityTargetSchema,
  ref: z.string().nullable(),
  hidden: z.boolean().optional(),
});
export type PerformVisibilityInput = z.infer<typeof performVisibilityInputSchema>;
